import React, { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useEntityNavigate as useNavigate } from '../../navigation/navigation'
import api from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { Button, Input, Select, Textarea } from '../../components/ui'
import { translateApiError } from '../../utils/errorMessages'
import './Company.css'

const DEFAULT_EMPLOYEE_TYPES = ['наемный', 'артельщик', 'ИП']

function EmployeeEdit() {
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const isNew = location.pathname.endsWith('/new') || id === 'new'
  const editId = isNew ? null : id
  const isManagement = user?.role === 'admin' || user?.role === 'management'

  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [originalFullName, setOriginalFullName] = useState('')
  const [employeeTypeOptions, setEmployeeTypeOptions] = useState(DEFAULT_EMPLOYEE_TYPES)
  const [formData, setFormData] = useState({
    full_name: '',
    position: '',
    employee_type: '',
    share_percentage: '',
    tg_id: '',
    tg_account: '',
    bot_communication_notes: '',
    is_budget_signer: false,
  })
  const normalizedCurrentType = String(formData.employee_type || '').trim()
  const effectiveEmployeeTypeOptions = normalizedCurrentType && !employeeTypeOptions.includes(normalizedCurrentType)
    ? [normalizedCurrentType, ...employeeTypeOptions]
    : employeeTypeOptions

  useEffect(() => {
    if (!editId || !isManagement) return
    fetchEmployee()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, isManagement])

  useEffect(() => {
    if (!isManagement) return
    loadEmployeeTypeOptions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManagement])

  const fetchEmployee = async () => {
    try {
      setLoading(true)
      const response = await api.get(`/company/employees/${editId}`)
      const employee = response?.data?.employee
      if (!employee) {
        alert('Сотрудник не найден')
        navigate('/company/team')
        return
      }
      setFormData({
        full_name: employee.full_name || '',
        position: employee.position || '',
        employee_type: employee.employee_type || '',
        share_percentage: employee.share_percentage ?? '',
        tg_id: employee.tg_id || '',
        tg_account: employee.tg_account || '',
        bot_communication_notes: employee.bot_communication_notes || '',
        is_budget_signer: Boolean(employee.is_budget_signer),
      })
      setOriginalFullName(employee.full_name || '')
    } catch (error) {
      alert('Ошибка загрузки сотрудника: ' + translateApiError(error.response?.data?.error || error.message))
      navigate('/company/team')
    } finally {
      setLoading(false)
    }
  }

  const loadEmployeeTypeOptions = async () => {
    try {
      const response = await api.get('/admin/reference')
      const refs = response?.data?.references || {}
      const values = refs['типы сотрудников'] || []
      const names = values
        .map((item) => (item?.name || '').trim())
        .filter(Boolean)
      if (names.length > 0) {
        setEmployeeTypeOptions(Array.from(new Set(names)))
      } else {
        setEmployeeTypeOptions(DEFAULT_EMPLOYEE_TYPES)
      }
    } catch (_) {
      setEmployeeTypeOptions(DEFAULT_EMPLOYEE_TYPES)
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    const fullName = formData.full_name.trim()
    if (!fullName) {
      alert('Укажите ФИО сотрудника')
      return
    }

    const shareRaw = String(formData.share_percentage ?? '').trim()
    const shareValue = shareRaw === '' ? null : Number(shareRaw.replace(',', '.'))
    if (shareRaw !== '' && !Number.isFinite(shareValue)) {
      alert('Укажите корректное значение для % пая')
      return
    }
    if (shareValue !== null && (shareValue < 0 || shareValue > 100)) {
      alert('% пая должен быть в диапазоне от 0 до 100')
      return
    }

    const payload = {
      full_name: fullName,
      position: formData.position.trim() || null,
      employee_type: formData.employee_type.trim() || null,
      share_percentage: shareValue,
      tg_id: formData.tg_id.trim() || null,
      tg_account: formData.tg_account.trim() || null,
      bot_communication_notes: formData.bot_communication_notes.trim() || null,
      is_budget_signer: Boolean(formData.is_budget_signer),
    }

    try {
      setSaving(true)
      let response
      if (editId) {
        response = await api.put(`/company/employees/${editId}`, payload)
      } else {
        response = await api.post('/company/employees', payload)
      }

      const savedEmployee = response?.data?.employee
      const targetName = savedEmployee?.id || editId || savedEmployee?.full_name || fullName
      navigate(`/company/team/${encodeURIComponent(targetName)}`)
    } catch (error) {
      alert('Ошибка сохранения сотрудника: ' + translateApiError(error.response?.data?.error || error.message))
    } finally {
      setSaving(false)
    }
  }

  if (!isManagement) {
    return (
      <div className="company-page">
        <div className="page-header">
          <Button variant="ghost" onClick={() => navigate('/company/team')} className="btn-back">
            ← Назад
          </Button>
          <h1>Редактирование сотрудника</h1>
        </div>
        <p className="info-note">Только роль «Руководство» может создавать, редактировать и удалять сотрудников.</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="company-page">
        <div className="loading">Загрузка...</div>
      </div>
    )
  }

  return (
    <div className="company-page">
      <div className="page-header">
        <Button
          variant="ghost"
          onClick={() => navigate(editId ? `/company/team/${encodeURIComponent(editId || originalFullName || formData.full_name)}` : '/company/team')}
          className="btn-back"
        >
          ← Назад
        </Button>
        <h1>{isNew ? 'Создание сотрудника' : 'Редактирование сотрудника'}</h1>
      </div>

      <div className="form-card">
        <form onSubmit={handleSubmit} className="department-form">
          <h2>{isNew ? 'Новый сотрудник' : 'Редактирование сотрудника'}</h2>

          <div className="form-group">
            <label htmlFor="employee-full-name">ФИО</label>
            <Input
              id="employee-full-name"
              type="text"
              value={formData.full_name}
              onChange={(e) => setFormData((prev) => ({ ...prev, full_name: e.target.value }))}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="employee-position">Должность</label>
            <Input
              id="employee-position"
              type="text"
              value={formData.position}
              onChange={(e) => setFormData((prev) => ({ ...prev, position: e.target.value }))}
              placeholder="Свободный текст"
            />
          </div>

          <div className="form-group">
            <label htmlFor="employee-type">ТИП</label>
            <Select
              id="employee-type"
              value={formData.employee_type}
              onChange={(e) => setFormData((prev) => ({ ...prev, employee_type: e.target.value }))}
            >
              <option value="">— Выберите —</option>
              {effectiveEmployeeTypeOptions.map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </Select>
          </div>

          <div className="form-group">
            <label htmlFor="employee-share-percentage">% пая</label>
            <Input
              id="employee-share-percentage"
              type="number"
              min="0"
              max="100"
              step="0.0001"
              value={formData.share_percentage}
              onChange={(e) => setFormData((prev) => ({ ...prev, share_percentage: e.target.value }))}
              placeholder="Например: 2.4"
            />
          </div>

          <div className="form-group">
            <label htmlFor="employee-tg-id">TG ID</label>
            <Input
              id="employee-tg-id"
              type="text"
              value={formData.tg_id}
              onChange={(e) => setFormData((prev) => ({ ...prev, tg_id: e.target.value }))}
              placeholder="Например: 123456789"
            />
          </div>

          <div className="form-group">
            <label htmlFor="employee-tg-account">TG аккаунт</label>
            <Input
              id="employee-tg-account"
              type="text"
              value={formData.tg_account}
              onChange={(e) => setFormData((prev) => ({ ...prev, tg_account: e.target.value }))}
              placeholder="@username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="employee-bot-communication-notes">Как общаться с сотрудником</label>
            <Textarea
              id="employee-bot-communication-notes"
              rows={4}
              value={formData.bot_communication_notes}
              onChange={(e) => setFormData((prev) => ({ ...prev, bot_communication_notes: e.target.value }))}
              placeholder="Стиль общения, тон, ограничения и предпочтения"
            />
          </div>

          <div className="form-group">
            <label className="checkbox-inline" htmlFor="employee-budget-signer">
              <input
                id="employee-budget-signer"
                type="checkbox"
                checked={formData.is_budget_signer}
                onChange={(e) => setFormData((prev) => ({ ...prev, is_budget_signer: e.target.checked }))}
              />
              <span>Подписант бюджета</span>
            </label>
          </div>

          <div className="form-actions">
            <div className="button-group">
              <Button type="button" variant="secondary" onClick={() => navigate('/company/team')}>
                Отмена
              </Button>
              <Button type="submit" variant="primary" disabled={saving}>
                {saving ? 'Сохранение...' : (isNew ? 'Создать' : 'Сохранить')}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EmployeeEdit
